import { useEffect, useState } from "react";

export const MODELS_LIST_PAGE_SIZES = [10, 15, 25] as const;
export type ModelsListPageSize = (typeof MODELS_LIST_PAGE_SIZES)[number];

const DEFAULT_PAGE_SIZE: ModelsListPageSize = 10;

export type ModelsListPagination<T> = {
  page: number;
  pageSize: ModelsListPageSize;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  pageItems: T[];
  setPage: (next: number) => void;
  setPageSize: (next: ModelsListPageSize) => void;
};

export function useManufacturingModelsListPagination<T>(
  items: readonly T[],
): ModelsListPagination<T> {
  const [pageSize, setPageSizeState] = useState<ModelsListPageSize>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (page > 0 && page * pageSize >= items.length) setPage(0);
  }, [items.length, page, pageSize]);

  function setPageSize(next: ModelsListPageSize) {
    setPageSizeState(next);
    setPage(0);
  }

  const pageCount = items.length > 0 ? Math.ceil(items.length / pageSize) : 1;
  const safePage = Math.min(page, Math.max(0, pageCount - 1));
  const rangeStart = items.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd = Math.min((safePage + 1) * pageSize, items.length);
  const pageItems = items.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return {
    page: safePage,
    pageSize,
    pageCount,
    rangeStart,
    rangeEnd,
    pageItems,
    setPage,
    setPageSize,
  };
}
